import { DragAndDroppedProjectToShareIds } from '../hooks/useShareDragAndDrop'
import { useParams } from 'react-router'
import {
  readStorageItems,
  removeFromStorageItemsList,
  RouteParams,
} from '@sketch/modules-common'
import { useGenerateFolderName } from './useGenerateFolderName'
import { useCreateProject } from '../../projects/hooks/useCreateProject'
import {
  GetStorageItemsDocument,
  GetStorageItemsQueryVariables,
  useGetProjectDetailsQuery,
} from '@sketch/gql-types'
import produce from 'immer'

interface UseDropProjectToShareProps {
  projectIdentifier?: string
  storageItemsQueryVariables: GetStorageItemsQueryVariables
}

// Creates a new nested project with the project dragged and share dropped
export const useDropProjectToShare = ({
  projectIdentifier,
  storageItemsQueryVariables,
}: UseDropProjectToShareProps) => {
  const { workspaceId } =
    useParams<RouteParams<'WORKSPACE_PROJECT' | 'WORKSPACE_DRAFTS'>>()

  // We need the project info for the optimistic response, this data it's already in cache
  const { data: currentProject } = useGetProjectDetailsQuery({
    variables: { projectIdentifier: projectIdentifier || '' },
    skip: !projectIdentifier,
  })

  const { createProject } = useCreateProject()

  const generateFolderName = useGenerateFolderName(
    storageItemsQueryVariables,
    projectIdentifier
  )

  const newFolderName = generateFolderName()

  const dropProjectToShare = async ({
    draggedProjectId,
    droppedShareId,
  }: DragAndDroppedProjectToShareIds) => {
    // we need a valid project id to create a nested project
    if (!projectIdentifier) {
      return
    }

    // Create nested project with both share and project inside
    createProject({
      variables: {
        workspaceId,
        parentProjectIdentifier: projectIdentifier,
        name: newFolderName,
        description: '',
        storageItems: [
          { identifier: draggedProjectId, type: 'PROJECT' },
          { identifier: droppedShareId, type: 'SHARE' },
        ],
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        createWorkspaceProject: {
          __typename: 'CreateWorkspaceProjectResponse',
          project: {
            __typename: 'Project',
            archivedAt: null,
            deletedAt: null,
            description: null,
            userCanLeave: false,
            projectPrivacyIcon: 'WORKSPACE_MEMBERS',
            identifier: 'new-project-id',
            name: newFolderName,
            parentProjectIdentifier: projectIdentifier,
            pinnedByCurrentUserAt: null,
            projectType: 'STANDARD',
            parentProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: currentProject?.project?.name ?? '',
              nestedProjectsCount:
                currentProject?.project?.nestedProjectsCount ?? 0,
              sharesCount: (currentProject?.project?.sharesCount ?? 0) - 1,
            },
            topLevelProject: {
              __typename: 'Project',
              identifier: projectIdentifier,
              name: '',
              workspaceAccessLevel:
                currentProject?.project?.workspaceAccessLevel ?? 'NONE',
            },
            workspaceAccessLevel:
              currentProject?.project?.workspaceAccessLevel ?? 'NONE',
          },
        },
      },
      update: (cache, { data }) => {
        // The strategy is to create the new project as quickly as possible to
        // give a sense of speed (that's possible because we are using an
        // optimistic response), and after the mutation is completed, the
        // nested project will be filled with the real data.
        const storageItems = readStorageItems(cache, storageItemsQueryVariables)

        const newProject = data?.createWorkspaceProject.project

        const newStorageItems = produce(storageItems, draft => {
          if (
            !draft?.project?.storageItems ||
            !newProject ||
            newProject.__typename !== 'Project'
          ) {
            return
          }

          draft.project.storageItems.meta.nestedProjectsCount += 1
          draft.project.storageItems.meta.totalCount += 1

          draft.project.storageItems.entries.unshift({
            ...newProject,
            sharesCount: 1,
            nestedProjectsCount: 1,
            previews: [],
          })

          draft.project.storageItems.entries.sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        })

        cache.writeQuery({
          query: GetStorageItemsDocument,
          variables: storageItemsQueryVariables,
          data: newStorageItems,
        })

        // We also want to remove the storage items from the parent project
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Project',
            identifier: draggedProjectId,
          },
        })
        removeFromStorageItemsList({
          cache,
          target: {
            __typename: 'Share',
            identifier: droppedShareId,
          },
        })
      },
    })
  }

  return dropProjectToShare
}
